import { isBoolean, sortBy } from 'lodash';
import { catchError, map, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';

import {
  QuestGQL,
  QuestType,
  WalletTypeEnum,
  QuestActivityType,
  QuestPerformedType,
  QuestAnalyticsType,
  SuccessResponseDtoType,
  UpsertQuestAnalyticsGQL,
  QuestAnalyticsUpdateInput,
  QuestionWithResponsesStatsType,
  GetQuestActivitiesByQuestCacheGQL,
  PerformNonPredefinedQuestByUserGQL,
  GetQuestsByTargetAndUserAudiencePaginatedGQL,
  GetQuestionsbyFormWithResponsesStatsPaginatedGQL,
  GetComingSoonQuestsByTargetAndUserAudiencePaginatedGQL,
  GetQuestsByTargetAndUserAudienceWithLinkedAccountsPaginatedGQL,
  GetComingSoonQuestsByTargetAndUserAudienceWithLinkedAccountsPaginatedGQL,
} from '@sifca-monorepo/widget-generator';
import { MobileAuthService } from './auth.service';
import { AnalyticsService } from './analytics.service';

@Injectable({
  providedIn: 'root',
})
export class MobileQuestsService {
  private isLast: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private loadingQuests: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private isLastHomeQuests: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private loadingActivitiesByQuest: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private homeQuests: BehaviorSubject<QuestPerformedType[] | QuestType[]> = new BehaviorSubject(null);
  private selectedQuestActivity: BehaviorSubject<QuestActivityType> = new BehaviorSubject<QuestActivityType>(null);
  private questActivitesByQuest: BehaviorSubject<QuestActivityType[]> = new BehaviorSubject<QuestActivityType[]>(null);
  private quest: BehaviorSubject<QuestPerformedType | QuestType> = new BehaviorSubject<QuestPerformedType | QuestType>(null);
  private questions: BehaviorSubject<QuestionWithResponsesStatsType[]> = new BehaviorSubject<QuestionWithResponsesStatsType[]>(null);
  private quests: BehaviorSubject<QuestPerformedType[] | QuestType[]> = new BehaviorSubject<QuestPerformedType[] | QuestType[]>(null);
  private initialQuests: BehaviorSubject<QuestPerformedType[] | QuestType[]> = new BehaviorSubject<QuestPerformedType[] | QuestType[]>(null);

  reversed = false;
  performed = false;
  pageIndex: number = 0;
  pageLimit: number = 3;

  get homeQuests$(): Observable<QuestPerformedType[] | QuestType[]> {
    return this.homeQuests.asObservable();
  }
  set homeQuests$(value: any) {
    this.homeQuests.next(value);
  }

  get initialQuests$(): Observable<QuestPerformedType[] | QuestType[]> {
    return this.initialQuests.asObservable();
  }
  set initialQuests$(value: any) {
    this.initialQuests.next(value);
  }

  get quest$(): Observable<QuestPerformedType | QuestType> {
    return this.quest.asObservable();
  }
  set quest$(value: any) {
    this.quest.next(value);
  }

  get quests$(): Observable<QuestPerformedType[] | QuestType[]> {
    return this.quests.asObservable();
  }
  set quests$(value: any) {
    this.quests.next(value);
  }

  get loadingQuests$(): Observable<boolean> {
    return this.loadingQuests.asObservable();
  }
  set loadingQuests$(value: any) {
    this.loadingQuests.next(value);
  }

  get isLast$(): Observable<boolean> {
    return this.isLast.asObservable();
  }
  set isLast$(value: any) {
    this.isLast.next(value);
  }

  get isLastHomeQuests$(): Observable<boolean> {
    return this.isLastHomeQuests.asObservable();
  }
  set isLastHomeQuests$(value: any) {
    this.isLastHomeQuests.next(value);
  }

  get questActivitesByQuest$(): Observable<QuestActivityType[]> {
    return this.questActivitesByQuest.asObservable();
  }
  set questActivitesByQuest$(value: any) {
    this.questActivitesByQuest.next(value);
  }

  get selectedQuestActivity$(): Observable<QuestActivityType> {
    return this.selectedQuestActivity.asObservable();
  }
  set selectedQuestActivity$(value: any) {
    this.selectedQuestActivity.next(value);
  }

  get questions$(): Observable<QuestionWithResponsesStatsType[]> {
    return this.questions.asObservable();
  }
  set questions$(value: any) {
    this.questions.next(value);
  }

  get loadingActivitiesByQuest$(): Observable<boolean> {
    return this.loadingActivitiesByQuest.asObservable();
  }
  set loadingActivitiesByQuest$(value: any) {
    this.loadingActivitiesByQuest.next(value);
  }

  constructor(
    private questGQL: QuestGQL,
    private analytics: AnalyticsService,
    private authService: MobileAuthService,
    private upsertQuestAnalyticsGQL: UpsertQuestAnalyticsGQL,
    private getQuestActivitiesByQuestGQL: GetQuestActivitiesByQuestCacheGQL,
    private performNonPredefinedQuestByUserGQL: PerformNonPredefinedQuestByUserGQL,
    private getQuestsByTargetAndUserAudiencePaginatedGQL: GetQuestsByTargetAndUserAudiencePaginatedGQL,
    private getQuestionsbyFormWithResponsesStatsPaginatedGQL: GetQuestionsbyFormWithResponsesStatsPaginatedGQL,
    private getComingSoonQuestsByTargetAndUserAudiencePaginatedGQL: GetComingSoonQuestsByTargetAndUserAudiencePaginatedGQL,
    private getQuestsByTargetAndUserAudienceWithLinkedAccountsPaginatedGQL: GetQuestsByTargetAndUserAudienceWithLinkedAccountsPaginatedGQL,
    private getComingSoonQuestsByTargetAndUserAudienceWithLinkedAccountsPaginatedGQL: GetComingSoonQuestsByTargetAndUserAudienceWithLinkedAccountsPaginatedGQL,
  ) {}

  getPerformQuestAnalytics() {
    combineLatest([this.authService.currentUser$, this.quest$])
      .pipe(take(1))
      .subscribe(([user, quest]) => {
        if (!user || !quest) return;
        const rem = quest?.remuneration.find((r) => r.wallet.walletType === WalletTypeEnum.QUANTITATIVE);
        this.analytics.trackEvent('quest_performed', {
          app_id: localStorage.getItem('posId'),
          user_id: user.id,
          quest_id: quest.id,
          quest_type: quest.questType?.title,
          points_earned: rem?.amount,
          language: localStorage.getItem('elvklang'),
          theme: localStorage.getItem('elvktheme'),
          non_personalized_ads: 1,
        });
      });
  }

  upsertQuestAnalytics(input: QuestAnalyticsUpdateInput): Observable<QuestAnalyticsType> {
    return this.upsertQuestAnalyticsGQL
      .mutate({
        input: { ...input, quest: input.quest, sessionId: localStorage.getItem('sessionId'), target: { pos: localStorage.getItem('posId') } },
      })
      .pipe(
        map(({ data }: any) => {
          return data.upsertQuestAnalytics;
        }),
      );
  }

  getComingSoonQuestsByTargetAndUserAudienceWithLinkedAccountsPaginated(reset = true): Observable<QuestPerformedType[] | QuestType[]> {
    if (reset) {
      this.loadingQuests.next(true);
    }
    return this.getComingSoonQuestsByTargetAndUserAudienceWithLinkedAccountsPaginatedGQL
      .fetch({
        input: {
          reversed: this.reversed,
          target: { pos: localStorage.getItem('posId') },
        },
        pagination: { page: this.pageIndex, limit: this.pageLimit },
      })
      .pipe(
        map(({ data }: any) => {
          this.loadingQuests.next(false);
          this.isLast.next(data.getComingSoonQuestsByTargetAndUserAudienceWithLinkedAccountsPaginated.isLast);
          this.quests.next([...(this.quests.value || []), ...data.getComingSoonQuestsByTargetAndUserAudienceWithLinkedAccountsPaginated.objects]);
          return data.getComingSoonQuestsByTargetAndUserAudienceWithLinkedAccountsPaginated.objects;
        }),
      );
  }

  getQuestsByTargetAndUserAudienceWithLinkedAccountsPaginated(reset = true): Observable<QuestPerformedType[]> {
    if (reset) {
      this.loadingQuests.next(true);
    }
    return this.getQuestsByTargetAndUserAudienceWithLinkedAccountsPaginatedGQL
      .fetch({
        input: {
          reversed: this.reversed,
          ...(isBoolean(this.performed) ? { performed: this.performed } : {}),
          target: { pos: localStorage.getItem('posId') },
        },
        pagination: { page: this.pageIndex, limit: this.pageLimit },
      })
      .pipe(
        map(({ data }: any) => {
          this.loadingQuests.next(false);
          if (data) {
            this.isLast.next(data.getQuestsByTargetAndUserAudienceWithLinkedAccountsPaginated.isLast);
            const objects = data.getQuestsByTargetAndUserAudienceWithLinkedAccountsPaginated.objects;
            this.quests.next(reset ? objects : [...(this.quests.value || []), ...objects]);
            return objects;
          }
        }),
      );
  }

  getQuestsByTargetAndUserAudiencePaginated(pos?: string, reset = true, isInitial = false): Observable<QuestPerformedType[]> {
    if (reset) {
      this.loadingQuests.next(true);
    }
    return this.getQuestsByTargetAndUserAudiencePaginatedGQL
      .fetch({
        input: {
          reversed: this.reversed,
          ...(this.performed === true ? { performed: this.performed } : {}),
          target: { pos: pos || localStorage.getItem('posId') },
        },
        pagination: { page: this.pageIndex, limit: this.pageLimit },
      })
      .pipe(
        map(({ data }: any) => {
          this.loadingQuests.next(false);
          if (data) {
            if (isInitial) {
              this.initialQuests.next(data.getQuestsByTargetAndUserAudiencePaginated.objects);
            }
            this.isLast.next(data.getQuestsByTargetAndUserAudiencePaginated.isLast);
            const objects = data.getQuestsByTargetAndUserAudiencePaginated.objects;
            this.quests.next(reset ? objects : [...(this.quests.value || []), ...objects]);
            return objects;
          }
        }),
      );
  }

  getComingSoonQuestsByTargetAndUserAudiencePaginated(pos?: string, reset = true): Observable<QuestPerformedType[] | QuestType[]> {
    if (reset) {
      this.loadingQuests.next(true);
    }
    return this.getComingSoonQuestsByTargetAndUserAudiencePaginatedGQL
      .fetch({
        input: {
          reversed: this.reversed,
          target: { pos: pos || localStorage.getItem('posId') },
        },
        pagination: { page: this.pageIndex, limit: this.pageLimit },
      })
      .pipe(
        map(({ data }: any) => {
          this.loadingQuests.next(false);
          this.isLast.next(data.getComingSoonQuestsByTargetAndUserAudiencePaginated.isLast);
          const objects = data.getComingSoonQuestsByTargetAndUserAudiencePaginated.objects;
          this.quests.next(reset ? objects : [...(this.quests.value || []), ...objects]);
          return objects;
        }),
      );
  }

  performNonPredefinedQuestByUser(input: any): Observable<SuccessResponseDtoType> {
    this.loadingActivitiesByQuest.next(true);
    return this.performNonPredefinedQuestByUserGQL
      .mutate({ input, ...(localStorage.getItem('userToken') ? { userToken: localStorage.getItem('userToken') } : {}) })
      .pipe(
        take(1),
        catchError(() => {
          this.loadingActivitiesByQuest.next(false);
          return of(null);
        }),
        map(({ data }: any) => {
          this.loadingActivitiesByQuest.next(false);
          if (data) {
            this.quest.next({ ...this.quest.value, performed: true } as any);
            return data.performNonPredefinedQuestByUser;
          }
        }),
      );
  }

  getQuestById(id: string): Observable<QuestPerformedType | QuestType> {
    return this.questGQL.fetch({ id }).pipe(
      map(({ data }: any) => {
        this.quest.next(data?.quest);
        return data.quest;
      }),
    );
  }

  getQuestionsByForm(id: string): Observable<QuestionWithResponsesStatsType[]> {
    this.loadingActivitiesByQuest.next(true);
    return this.getQuestionsbyFormWithResponsesStatsPaginatedGQL.fetch({ id, pagination: { page: 0, limit: 20000 } }).pipe(
      map(({ data }: any) => {
        this.loadingActivitiesByQuest.next(false);
        this.questions.next(sortBy(data.getQuestionsbyFormWithResponsesStatsPaginated.objects, 'order'));
        return data.getQuestionsbyFormWithResponsesStatsPaginated.objects;
      }),
    );
  }

  getQuestActivitiesByQuest(quest: string): Observable<QuestActivityType[]> {
    this.loadingActivitiesByQuest.next(true);
    return this.getQuestActivitiesByQuestGQL.fetch({ quest }).pipe(
      map(({ data }: any) => {
        if (data) {
          /* this.quest.next(data.getQuestActivitiesByQuestCache?.[0]?.quest); */
          this.loadingActivitiesByQuest.next(false);
          this.questActivitesByQuest.next(data.getQuestActivitiesByQuestCache);
          return data.getQuestActivitiesByQuestCache;
        }
      }),
    );
  }
}
